import { getVendor, VENDORS } from '../../config/vendorRegistry';
import type { DomainConfiguration } from '../ConfigurationMapper';
import type { RulesContext } from './context';

/**
 * CONF-94. MLEC (Multi-Level Erasure Coding) gives one HDD per VPOD to a local
 * ZFS raidz1 parity, beneath the PanFS network-level D+P stripe.
 *
 * Which chassis qualify is a per-vendor decision recorded in the vendor registry
 * (`capabilities.mlecJbodSizes`, beside `supportsMLEC`): AIC 4U78 (78 drives) and
 * 4U108, and Supermicro 4U90. The 4U108 splits into 12 VPODs of exactly 9 HDDs, so
 * the loss is a clean 1/9 (11.1%). The 4U90 and 4U78 are 7.5 and 6.5 per VPOD on
 * paper — mixes of whole drives in hardware — but MLEC takes one drive from every
 * VPOD regardless, so 12 of the drives go to parity (13.3% and 15.4%) whatever the
 * split. The AIC 4U78 was added by CONF-222. Still excluded: the SMC 4U60 (20%)
 * and AIC's 30-drive SKU (40%).
 *
 * This module is the SINGLE definition of "can MLEC be on". Three consumers:
 * the R12 business rule (which clears the flag so the UI toggle snaps off), the
 * metrics seam in src/metrics/index.ts (which protects the rules-free path the
 * AI optimizer takes), and the two config cards (which disable the toggle).
 *
 * See docs/superpowers/specs/2026-09-17-conf-94-mlec-design.md sections 3 and 8.
 */

const knownVendor = (vendorId: string): any | null => {
  const upperVendorId = vendorId?.toUpperCase();
  const isKnownVendor = Object.prototype.hasOwnProperty.call(VENDORS, upperVendorId);
  return isKnownVendor ? (getVendor(vendorId) as any) : null;
};

/** Whether this vendor's hardware supports MLEC at all. */
const vendorSupportsMlec = (vendorId: string): boolean =>
  Boolean(knownVendor(vendorId)?.capabilities?.supportsMLEC);

/**
 * The JBOD sizes MLEC may be enabled on for this vendor, from the registry.
 * Empty for an unknown vendor, a vendor without MLEC, or one that declares
 * support but lists no chassis — never undefined, never a throw.
 */
export const mlecJbodSizes = (vendorId: string): number[] => {
  const capabilities = knownVendor(vendorId)?.capabilities;
  if (!capabilities?.supportsMLEC) return [];
  return Array.isArray(capabilities.mlecJbodSizes) ? [...capabilities.mlecJbodSizes] : [];
};

/**
 * The chassis name for a JBOD size as the configurator's own dropdown shows it:
 * the part of `jbodSizeLabels` before the " | " ("4U108 | x108 HDDs" -> "4U108").
 * When the vendor sells another JBOD in the same chassis (AIC's 30- and 78-drive
 * JBODs are both "4U78"), the drive count is added so the name picks out one:
 * "4U78 (x78 HDDs)". Falls back to the bare drive count for a size with no label.
 */
const chassisOf = (label: unknown): string =>
  (typeof label === 'string' ? label.split('|')[0]?.trim() ?? '' : '');

const jbodChassisName = (vendorId: string, jbodSize: number): string => {
  const labels: Record<string, unknown> = knownVendor(vendorId)?.capabilities?.jbodSizeLabels ?? {};
  const label = labels[jbodSize];
  const chassis = chassisOf(label);
  if (!chassis) return `${jbodSize}-drive`;
  const shared = Object.entries(labels)
    .some(([size, other]) => Number(size) !== jbodSize && chassisOf(other) === chassis);
  const drives = typeof label === 'string' ? label.split('|')[1]?.trim() : '';
  return shared && drives ? `${chassis} (${drives})` : chassis;
};

/**
 * Human-readable vendor name for UI copy. Deliberately does NOT go through
 * `getVendor` for an unknown id — `getVendor` warns and falls back to AIC's
 * config for anything it doesn't recognize, which would print "AIC" for a
 * typo'd or future vendor id instead of the id itself. Falls back to the raw
 * id for an unknown vendor; must never throw.
 */
const vendorDisplayName = (vendorId: string): string =>
  knownVendor(vendorId)?.displayName || vendorId;

/**
 * Whether MLEC may be enabled for this vendor and JBOD size.
 * Unknown vendors and missing JBOD sizes are ineligible rather than an error —
 * this runs on every config change and must never throw.
 */
export const mlecEligible = (vendorId: string, jbodSize: number): boolean =>
  mlecJbodSizes(vendorId).includes(jbodSize);

/**
 * Why MLEC is unavailable, as UI copy, or null when it is available.
 * The vendor reason wins when both apply: a vendor that does not ship MLEC cannot
 * be fixed by changing the JBOD, so telling the user to switch chassis would be
 * misleading.
 */
export const mlecIneligibleReason = (vendorId: string, jbodSize: number): string | null => {
  if (!vendorSupportsMlec(vendorId)) {
    return `Not available on ${vendorDisplayName(vendorId)} hardware`;
  }
  const sizes = mlecJbodSizes(vendorId);
  if (!sizes.includes(jbodSize)) {
    const names = sizes.map((size) => jbodChassisName(vendorId, size)).join(' or ');
    return `Requires the ${names} JBOD`;
  }
  return null;
};

/**
 * CONF-220. The share of HDD capacity MLEC costs on this vendor and JBOD, or null
 * when MLEC cannot be on. MLEC takes one HDD from each of the node's VPODs, so the
 * loss is vpodsPerNode / jbodSize: 1/9 on the AIC 4U108, 12/78 on the 4U78, 12/90
 * on the Supermicro 4U90. This is the same "one drive less per VPOD" that
 * raw2Useable deducts, read from the same productDb constant. It is a share of the
 * HDD tier only; the VPOD's NVMe is untouched.
 */
export const mlecHddLossFraction = (vendorId: string, jbodSize: number): number | null => {
  if (!mlecEligible(vendorId, jbodSize)) return null;
  const vpodsPerNode = Number(knownVendor(vendorId)?.databases?.products?.vpodsPerNode);
  return vpodsPerNode > 0 ? vpodsPerNode / jbodSize : null;
};

/** CONF-220. mlecHddLossFraction as UI copy ("11.1% loss of HDD capacity"), or null. */
export const mlecHddLossLabel = (vendorId: string, jbodSize: number): string | null => {
  const fraction = mlecHddLossFraction(vendorId, jbodSize);
  return fraction === null ? null : `${(fraction * 100).toFixed(1)}% loss of HDD capacity`;
};

/**
 * R12 (CONF-94). Clear hybrid MLEC when the current vendor and JBOD cannot support it.
 *
 * Only ever turns the flag OFF. Eligibility permits MLEC; it does not select it —
 * turning it on is always the user's action.
 *
 * SHARED rule: hybridMLEC is a single flat field serving both the Base and the
 * Expansion views, so this must run for both system types. It reads jbodSize,
 * but no rule in this package writes jbodSize — applyJbodEol (R2) only ever
 * changes hddCapacity — so R12's position relative to R2 is not load-bearing.
 */
export function applyMlecEligibility(next: DomainConfiguration, ctx: RulesContext): void {
  const hybrid = next.system.hybrid;
  if (hybrid.mlec && !mlecEligible(ctx.vendorId, hybrid.jbodSize)) {
    hybrid.mlec = false;
  }
}
